'use client';

import { NextIntlClientProvider } from 'next-intl';
import { useEffect, useState } from 'react';
import { captureIncomingLocale } from '@/lib/i18n/incomingLocale';
import { setImperativeMessages } from '@/lib/i18n/imperative';
import {
  DEFAULT_MESSAGE_LOCALE,
  type MessageLocale,
} from '@/lib/i18n/messageLocale';
import { useMessageLanguage } from '@/lib/i18n/useLocale';
import en from '../../../messages/en.json';

// R8 i18n Phase 0 — client-side message provider.
//
// Usage mode (deliberate, do not "fix" toward the next-intl getting-started
// docs): manually constructed NextIntlClientProvider with explicit locale +
// messages props. NO next-intl/plugin, NO i18n/request.ts, NO middleware, NO
// [locale] route segment — those assume request-time rendering, which
// `output: 'export'` (static export served by Angie) cannot satisfy.
// Design: docs/plans/2026-07-05-i18n-harness-design.md

type Messages = typeof en;

// fr/de/es load as dynamic imports (separate chunks) so non-English catalogs
// never weigh down the default bundle; en is the always-present fallback.
// The casts below: translated catalogs lag en structurally while a sweep is in
// flight (en is authored first; MT fills the targets before merge), so
// `typeof`-equality between the JSON modules is NOT a type-system invariant.
// Key-set parity is enforced where it actually holds — the catalogs.test.ts CI
// gate — and runtime lookup of a missing key falls back to English via
// getMessageFallback below.
const loaders: Record<Exclude<MessageLocale, 'en'>, () => Promise<{ default: Messages }>> = {
  fr: () => import('../../../messages/fr.json') as unknown as Promise<{ default: Messages }>,
  de: () => import('../../../messages/de.json') as unknown as Promise<{ default: Messages }>,
  es: () => import('../../../messages/es.json') as unknown as Promise<{ default: Messages }>,
};

function onError(err: unknown) {
  // A missing translation is a catalog bug, not a crash: log outside prod,
  // stay silent in prod (getMessageFallback below keeps rendering English).
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[i18n]', err);
  }
}

function englishFallback(namespace: string | undefined, key: string): string {
  const path = namespace ? `${namespace}.${key}` : key;
  let node: unknown = en;
  for (const part of path.split('.')) {
    if (typeof node !== 'object' || node === null || !(part in node)) return path;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : path;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  // Persist a marketing-site `?lang=` handoff once, so it survives the
  // register↔login cross-links that drop the query string within this tab.
  useEffect(() => {
    captureIncomingLocale();
  }, []);

  // Explicit ui_language preference, else a `?lang=` handoff, else the locale's
  // language prefix on 'auto' (see useMessageLanguage).
  const target = useMessageLanguage();

  // HYDRATION INVARIANT (design §B2 — load-bearing, pinned by test): the
  // first client render MUST be English to match the build-time static HTML.
  // useLocale() can already resolve fr/de synchronously via navigator.language
  // before the WS settings sync, so `active` initializes to the statically
  // imported en object UNCONDITIONALLY — never derived from `target`. The
  // target catalog applies in a later render, after its dynamic import
  // resolves. Only misfires on non-English machines, so don't rely on manual
  // review to protect this.
  const [active, setActive] = useState<{ locale: MessageLocale; messages: Messages }>({
    locale: DEFAULT_MESSAGE_LOCALE,
    messages: en,
  });

  useEffect(() => {
    if (target === 'en') {
      setActive({ locale: DEFAULT_MESSAGE_LOCALE, messages: en });
      return;
    }
    let cancelled = false;
    loaders[target]()
      .then((mod) => {
        if (!cancelled) setActive({ locale: target, messages: mod.default });
      })
      .catch(() => {
        // Catalog chunk failed to load (offline, deploy skew) — stay on en.
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  // The root layout hardcodes lang="en" at build time; keep the live document
  // language honest for screen readers / translation heuristics.
  useEffect(() => {
    document.documentElement.lang = active.locale;
  }, [active.locale]);

  // R8 Phase 1b/1c — keep the imperative translator (used from non-React
  // code: friendlyMessage, toastStore, wsManager request rejections, the
  // Zustand stores and CM6 editor sources — and, during render, by the
  // progress tickers resolving backend LocalizedCopy) in sync with the active
  // catalog. Synced DURING RENDER, not in an effect: an effect runs after the
  // children have rendered, so on a locale switch a child re-rendered by its
  // own useTranslations read the PREVIOUS catalogue from the bridge, and
  // nothing re-rendered it once the effect swapped it — a settled failure line
  // stayed in the old language indefinitely (CHAT-43c I2). Safe in render
  // because setImperativeMessages is idempotent: the same locale + the same
  // catalog object is a no-op, so StrictMode's double render costs nothing.
  setImperativeMessages(active.locale, active.messages as unknown as Record<string, unknown>);

  return (
    <NextIntlClientProvider
      locale={active.locale}
      messages={active.messages}
      onError={onError}
      getMessageFallback={({ namespace, key }) => englishFallback(namespace, key)}
    >
      {children}
    </NextIntlClientProvider>
  );
}
